/// <reference path="./i18next.d.ts" />
/*
 * i18n — the single source of UI language. Shaped like theme.ts (init / get /
 * set / onChange) for consistency, with i18next under the hood for the actual
 * lookups, interpolation, and plural rules.
 *
 * LOAD PERF (load-bearing): only English is bundled (the fallback); es/fr/de/ja
 * are each their own dynamic-import() chunk, fetched ONLY when first selected —
 * so first paint never downloads a language the user isn't using. Switching is a
 * one-time fetch (then cached) + an in-place re-render of live components; no
 * reload, no flash.
 *
 * REACTIVITY: Lit components add a LocaleController (auto re-render on change);
 * the vanilla symbol grid / managers subscribe via onLocaleChange.
 */
import i18next from 'i18next';
import en from './locales/en';
// Re-export the Lit reactive controller so the single `@vitesse/base-ui/i18n`
// specifier carries the whole i18n surface (t / formatNumber / onLocaleChange /
// LocaleController) — consumers never need a deep `./i18n/LocaleController` path
// (which isn't an exports key). The barrel already re-exports both.
export { LocaleController } from './LocaleController';
export const LOCALES = [
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Español' },
    { code: 'fr', label: 'Français' },
    { code: 'de', label: 'Deutsch' },
    { code: 'ja', label: '日本語' },
];
const STORAGE_KEY = 'app-locale';
const listeners = new Set();
// Per-locale lazy loaders — each import() is its own chunk (en is bundled).
const loaders = {
    es: () => import('./locales/es'),
    fr: () => import('./locales/fr'),
    de: () => import('./locales/de'),
    ja: () => import('./locales/ja'),
};
function isLocale(v) {
    return v != null && LOCALES.some((l) => l.code === v);
}
function detect() {
    let saved = null;
    try {
        saved = localStorage.getItem(STORAGE_KEY);
    }
    catch {
        /* private mode / storage disabled — fall through to the browser language */
    }
    if (isLocale(saved))
        return saved;
    const nav = navigator.language.slice(0, 2);
    return isLocale(nav) ? nav : 'en';
}
export function getLocale() {
    return i18next.language || 'en';
}
/** Switch language. Lazy-loads + caches the dictionary on first use, persists the
 *  choice, then notifies subscribers (Lit controllers + vanilla listeners). */
export async function setLocale(locale) {
    if (locale !== 'en' && !i18next.hasResourceBundle(locale, 'translation')) {
        const mod = await loaders[locale]();
        i18next.addResourceBundle(locale, 'translation', mod.default);
    }
    await i18next.changeLanguage(locale);
    document.documentElement.lang = locale;
    try {
        localStorage.setItem(STORAGE_KEY, locale);
    }
    catch {
        /* private mode / storage disabled — the switch applies, just won't persist */
    }
    for (const cb of listeners)
        cb(locale);
}
/** Subscribe to locale changes; returns an unsubscribe fn (mirrors onThemeChange). */
export function onLocaleChange(cb) {
    listeners.add(cb);
    return () => listeners.delete(cb);
}
/** Call once at boot, BEFORE mounting the app — i18next.init is async, so the
 *  first render must wait for it to land in the stored/detected language. */
export async function initI18n() {
    await i18next.init({
        lng: 'en',
        fallbackLng: 'en',
        resources: { en: { translation: en } },
        interpolation: { escapeValue: false }, // textContent/Lit escape; don't double-escape
        returnNull: false,
    });
    const locale = detect();
    if (locale !== 'en')
        await setLocale(locale);
    else
        document.documentElement.lang = 'en';
}
/** The translation function. Keys are type-checked against en (see i18next.d.ts):
 *  t('home.greeting', { name }) compiles, t('home.typo') does not. */
export const t = i18next.t.bind(i18next);
// The typed `t` above checks every key at compile time — which is the point, and
// which makes it unusable for a key built from DATA at runtime. The two readers
// below are for exactly that case: a connector's field label, keyed by the field
// key the integrations service sent. They are the ONLY untyped lookups, and both
// return the caller's fallback rather than the key when nothing is found, so a
// key the locale files do not know renders as the server's own English instead
// of as `integrationField.foo`.
const raw = i18next.t.bind(i18next);
/** A runtime-built key, or `fallback` when no locale has it. */
export function tOr(key, fallback) {
    return i18next.exists(key) ? raw(key) : fallback;
}
/** The ENGLISH text under a runtime-built key, or undefined. Used to decide
 *  whether a server-sent English label is the one a translation stands for —
 *  a connector that labels a shared key its own way keeps its own wording. */
export function tEn(key) {
    return i18next.exists(key, { lng: 'en' }) ? raw(key, { lng: 'en' }) : undefined;
}
/** Locale-aware number formatting (thousands separators, decimal sign). Use for
 *  UI-CHROME numbers — symbol counts, selected counts — NOT raw PLC values, which
 *  are precision-sensitive data shown verbatim. Memoised per (locale, opts) so the
 *  hot grid path never rebuilds a formatter. */
const nfCache = new Map();
export function formatNumber(n, opts) {
    const key = opts ? `${getLocale()}:${JSON.stringify(opts)}` : getLocale();
    let nf = nfCache.get(key);
    if (!nf) {
        nf = new Intl.NumberFormat(getLocale(), opts);
        nfCache.set(key, nf);
    }
    return nf.format(n);
}
//# sourceMappingURL=index.js.map